/**
 * The Skynet pixel palette.
 *
 * Accents come straight from the Leaselab brand kit (maroon, salmon, cyan,
 * lime and the two neutral ramps). Pixel art needs longer ramps than a brand
 * kit provides, so each brand hue gets extra shades, and the gaps a factory
 * needs (safety amber, error red, blueprint violet, rose, wood) are filled
 * with colours tuned to sit next to the brand hues.
 */
export const BRAND = {
  maroon: "#380029",
  maroon50: "#6F4A5E",
  maroon20: "#A68A9A",
  white: "#FFFFFF",
  salmon: "#FF705C",
  salmon50: "#FFA89A",
  salmon20: "#FFD6D1",
  cyan: "#00BDD9",
  cyan50: "#80D6E4",
  cyan20: "#C0EBF2",
  lime: "#B5EB6E",
  lime50: "#D7F5B7",
  lime20: "#ECFAD9",
  charcoal: "#1F2629",
  charcoal80: "#4D5457",
  charcoal50: "#8C9496",
  charcoal20: "#B8BCBD",
  silver: "#D1D4D4",
  silver50: "#E8E9E9",
  silver20: "#F3F4F4",
} as const;

export const PX = {
  // steel: brand neutrals extended down to an outline ink
  ink: "#0B0F11",
  steel950: "#121719",
  steel900: BRAND.charcoal,
  steel850: "#252D30",
  steel800: "#2C3438",
  steel700: "#384145",
  steel600: BRAND.charcoal80,
  steel500: "#6A7376",
  steel400: BRAND.charcoal50,
  steel300: BRAND.charcoal20,
  steel200: BRAND.silver,
  steel100: BRAND.silver50,
  steel50: BRAND.silver20,
  white: BRAND.white,

  // maroon / plum (brand dark)
  maroon950: "#1A0012",
  maroon900: "#26001C",
  maroon: BRAND.maroon,
  maroon700: "#561A44",
  plum: BRAND.maroon50,
  mauve: BRAND.maroon20,
  mauve100: "#D9C9D2",

  // salmon (brand primary)
  salmon900: "#6B1F24",
  salmon800: "#A63A33",
  salmon600: "#E0533F",
  salmon: BRAND.salmon,
  salmon300: BRAND.salmon50,
  salmon100: BRAND.salmon20,

  // cyan (brand secondary)
  cyan900: "#003845",
  cyan800: "#005E70",
  cyan600: "#0090A8",
  cyan: BRAND.cyan,
  cyan300: BRAND.cyan50,
  cyan100: BRAND.cyan20,

  // lime (brand tertiary)
  lime900: "#24401A",
  lime800: "#3F6B1F",
  lime600: "#7DB83E",
  lime: BRAND.lime,
  lime300: BRAND.lime50,
  lime100: BRAND.lime20,

  // gap: safety amber (hazard stripes, warnings)
  amber900: "#5C3A0E",
  amber800: "#9A5B12",
  amber600: "#E09A2A",
  amber: "#FFC94D",
  amber200: "#FFE6A3",

  // gap: error red, kept cooler than salmon so the two never read as the same thing
  red900: "#4A0E1C",
  red800: "#7A1426",
  red600: "#B8233A",
  red: "#E23D50",
  red300: "#FF8F99",

  // gap: blueprint violet (design, waiting for input)
  violet900: "#271A4D",
  violet800: "#3F2A78",
  violet600: "#6A4FB8",
  violet: "#A88BEB",
  violet200: "#D6C8F7",

  // gap: rose, the bridge between salmon and brand maroon
  rose900: "#4A0B2E",
  rose800: "#7A1F4C",
  rose600: "#B8396F",
  rose: "#E8619A",
  rose200: "#F7B3CF",

  // gap: orange between salmon and amber
  orange800: "#8A3A12",
  orange600: "#D2641F",
  orange: "#FF9447",
  orange200: "#FFC79E",

  // gap: wood and cardboard for crates and pallets
  wood900: "#2E1A14",
  wood800: "#4A2C22",
  wood600: "#7A4B33",
  wood: "#B07A4C",
  wood200: "#D9AE7A",

  // sky through the factory windows
  sky900: "#0E2A3A",
  sky: "#5FB8D0",
  sky200: "#A8E0EC",

  // crew skin and hair tones
  skin1: "#F6CDAE",
  skin2: "#D9A07A",
  skin3: "#A86E4A",
  skin4: "#6E4630",
  hair1: "#2B1B16",
  hair2: "#7A4B2A",
  hair3: "#E0B35A",
} as const;

export type PaletteName = keyof typeof PX;

/** Semantic UI tokens, mirrored as CSS variables in index.css. */
export const UI = {
  bg: PX.maroon950,
  panel: PX.steel900,
  panelDeep: PX.steel950,
  text: PX.steel50,
  textDim: PX.steel300,
  textMute: PX.steel400,
  primary: PX.salmon,
  info: PX.cyan,
  success: PX.lime,
  warning: PX.amber,
  danger: PX.red,
  input: PX.violet,
} as const;

// ---------------------------------------------------------------------------
// colour maths

interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function hexToRgb(hex: string): [number, number, number] {
  let value = hex.replace("#", "");
  if (value.length === 3)
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  const n = Number.parseInt(value.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const to = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();
}

function toHsl(hex: string): Hsl {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255) as [number, number, number];
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

function fromHsl({ h, s, l }: Hsl): string {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    hue < 60
      ? [c, x, 0]
      : hue < 120
        ? [x, c, 0]
        : hue < 180
          ? [0, c, x]
          : hue < 240
            ? [0, x, c]
            : hue < 300
              ? [x, 0, c]
              : [c, 0, x];
  return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}

/** Rotate a hue towards a target along the shortest arc. */
function towards(h: number, target: number, amount: number): number {
  const diff = ((target - h + 540) % 360) - 180;
  return h + Math.sign(diff) * Math.min(Math.abs(diff), amount);
}

export interface Ramp {
  deep: string;
  shadow: string;
  base: string;
  shine: string;
}

const rampCache = new Map<string, Ramp>();

/**
 * A four step pixel-art ramp for any colour: shadows lean towards violet and
 * highlights towards warm yellow (classic hue shifting), so configured station
 * colours always shade like hand-picked ones.
 */
export function ramp(hex: string): Ramp {
  const key = hex.toUpperCase();
  const cached = rampCache.get(key);
  if (cached) return cached;
  const c = toHsl(key);
  const result: Ramp = {
    deep: fromHsl({ h: towards(c.h, 275, 26), s: Math.min(1, c.s * 0.85), l: c.l * 0.32 }),
    shadow: fromHsl({ h: towards(c.h, 275, 12), s: Math.min(1, c.s * 0.95), l: c.l * 0.68 }),
    base: key,
    shine: fromHsl({ h: towards(c.h, 60, 10), s: c.s * 0.92, l: c.l + (1 - c.l) * 0.5 }),
  };
  rampCache.set(key, result);
  return result;
}

export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

export function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** Pick ink or white text for a background colour. */
export function readableOn(hex: string): string {
  const [r, g, b] = hexToRgb(hex);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? PX.maroon : PX.white;
}
